// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface LearningObjective {
  objective_statement: string;
  points_allocated?: number;
  problems_aligned?: string[];
}

interface LearningObjectivesPointsChartProps {
  objectives: LearningObjective[];
  documentCount?: number;
}

/**
 * Component to display a horizontal bar chart showing points distribution
 * across learning objectives for bulk analysis of exam files.
 * 
 * This component reads through ALL exam files in the bulk analysis and aggregates
 * the total points allocated to each learning objective across all exams.
 */
export function LearningObjectivesPointsChart({ 
  objectives, 
  documentCount 
}: LearningObjectivesPointsChartProps) {
  
  // Aggregate points across all learning objectives
  const chartData = useMemo(() => {
    if (!objectives || objectives.length === 0) {
      console.log('LearningObjectivesPointsChart: No objectives provided');
      return [];
    }

    console.log('LearningObjectivesPointsChart: Processing objectives', objectives);

    // Filter objectives that have points allocated
    const objectivesWithPoints = objectives
      .filter(obj => {
        const hasPoints = obj.points_allocated && obj.points_allocated > 0;
        if (!hasPoints) {
          console.log('Objective without points:', obj.objective_statement);
        }
        return hasPoints;
      })
      .map((obj, index) => ({
        name: obj.objective_statement, // Keep full name - will handle wrapping in chart
        fullName: obj.objective_statement,
        points: obj.points_allocated || 0,
        problemsAligned: obj.problems_aligned || [],
        index: index
      }))
      .sort((a, b) => b.points - a.points); // Sort by points descending

    console.log('LearningObjectivesPointsChart: Chart data prepared', objectivesWithPoints);
    return objectivesWithPoints;
  }, [objectives]);

  // Calculate total points
  const totalPoints = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.points, 0);
  }, [chartData]);

  // Don't render if no data
  if (chartData.length === 0) {
    return null;
  }

  // UMD Brand Color Palette - https://brand.umd.edu/colors
  const COLORS = [
    '#E21833', // UMD Red (Primary)
    '#FFD520', // UMD Gold/Yellow
    '#000000', // Black
    '#E03C31', // Red Orange
    '#BA1C21', // Dark Red
    '#FFCD00', // Bright Gold
    '#FDB714', // Orange
    '#6B6B6B', // Charcoal Gray
    '#9E9E9E', // Medium Gray
    '#CD7F32', // Bronze
    '#B8860B', // Dark Gold/Bronze
    '#A9A9A9'  // Light Gray
  ];

  // Custom tooltip with more details
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border-2 border-gray-300 rounded-lg shadow-lg max-w-md">
          <p className="font-semibold text-gray-900 mb-2">{data.fullName}</p>
          <div className="space-y-1">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Total Points:</span> {data.points}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Percentage of Exam:</span> {((data.points / totalPoints) * 100).toFixed(1)}%
            </p>
            {data.problemsAligned && data.problemsAligned.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-medium">Problems:</span> {data.problemsAligned.length} aligned
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid" style={{ overflow: 'visible' }}>
      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
        <span className="text-3xl mr-3">📊</span>
        Points Distribution by Learning Outcome
        {documentCount && documentCount > 1 && (
          <span className="text-sm text-gray-600 ml-3 font-normal">
            (Aggregated across {documentCount} exams)
          </span>
        )}
      </h2>

      <div className="mb-4 bg-gray-50 p-4 rounded-lg">
        <p className="text-sm text-gray-700">
          <span className="font-semibold">Total Points:</span> {totalPoints}
        </p>
        <p className="text-xs text-gray-600 mt-1">
          This chart shows how exam points are distributed across different learning outcomes.
        </p>
      </div>

      <style>{`
        @media print {
          .recharts-surface {
            overflow: visible !important;
          }
          .recharts-wrapper {
            overflow: visible !important;
          }
          .recharts-responsive-container {
            overflow: visible !important;
          }
        }
      `}</style>

      <div className="mt-6" style={{ width: '100%', height: Math.max(400, chartData.length * 70), overflow: 'visible' }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 250, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis 
              type="number" 
              label={{ value: 'Points', position: 'insideBottom', offset: -10 }}
            />
            <YAxis 
              type="category" 
              dataKey="name" 
              width={240}
              interval={0}
              tick={(props) => {
                const { x, y, payload } = props;
                const text = payload.value;
                const maxWidth = 230;
                const words = text.split(' ');
                const lines: string[] = [];
                let currentLine = '';

                words.forEach(word => {
                  const testLine = currentLine ? `${currentLine} ${word}` : word;
                  // Rough estimate: 7 pixels per character
                  if (testLine.length * 7 > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                  } else {
                    currentLine = testLine;
                  }
                });
                if (currentLine) {
                  lines.push(currentLine);
                }

                // Limit to 3 lines and add ellipsis if needed
                const displayLines = lines.slice(0, 3);
                if (lines.length > 3) {
                  displayLines[2] = displayLines[2].substring(0, 30) + '...';
                }

                return (
                  <g>
                    {displayLines.map((line, index) => (
                      <text
                        key={index}
                        x={x}
                        y={y + (index * 14) - ((displayLines.length - 1) * 7)}
                        textAnchor="end"
                        fill="#374151"
                        fontSize={11}
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                );
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              formatter={() => 'Points Allocated'}
              wrapperStyle={{ paddingTop: '20px' }}
            />
            <Bar 
              dataKey="points" 
              name="Points"
              radius={[0, 8, 8, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Highest Points</p>
          <p className="text-lg font-bold text-[#E21833]">
            {chartData[0]?.points || 0}
          </p>
          <p className="text-xs text-gray-600 mt-1 truncate">
            {chartData[0]?.name || 'N/A'}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Average Points</p>
          <p className="text-lg font-bold text-[#E21833]">
            {(totalPoints / chartData.length).toFixed(1)}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            per objective
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Objectives Covered</p>
          <p className="text-lg font-bold text-[#E21833]">
            {chartData.length}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            with points allocated
          </p>
        </div>
      </div>
    </div>
  );
}
